import dayjs from 'dayjs';
import { type SyntheticEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventListener, useOnClickOutside } from 'usehooks-ts';
import type { ContentInteractionType } from '@/components/conversations/conversation-list/message-list-item/message-content/content-common-props';
import useMessageInfo, {
  cannotForwardMessageType,
} from '@/components/conversations/conversation-list/message-list-item/use-message-info';
import useConversationStore, { useConversationHandlers } from '@/hooks/conversations/use-conversation-store';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useGroupMembers from '@/hooks/groups/use-group-members';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import cn from '@/utils/merge-class-names';
import MenuItem from './menu-item';

type ConversationMenuProps = {
  message: ConversationMessageTypeUnion;
  onClose: () => void;
};

export default function ConversationMenu({ onClose, message }: ConversationMenuProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();
  const { resetSelectedMessages } = useConversationStore();
  const { handleInteraction: onInteraction } = useConversationHandlers();
  const { isMine, isPending, sessionId, sessionType } = useMessageInfo(message);
  const { canManage: canEditGroup, checkIsMemberAdmin } = useGroupInfo(sessionType === 'group' ? sessionId : null);
  const { members, bannedMembers } = useGroupMembers(sessionType === 'group' ? sessionId : null);
  const isMuted = bannedMembers.some((bm) => bm.UserID === message?.UserID);
  const hasMemberInGroup = Boolean(message && message.UserID in members);
  const isMemberAdmin = checkIsMemberAdmin(message.UserID ?? 0);

  const handleInteraction = (action: ContentInteractionType) => (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!message?.ID) return;
    onInteraction(action);
    onClose();
  };

  useOnClickOutside(ref, () => resetSelectedMessages());
  useEventListener('wheel', () => resetSelectedMessages());
  useEventListener('resize', () => resetSelectedMessages());

  return (
    <div
      ref={isDesktop ? ref : undefined}
      className='flex w-36 flex-col items-stretch rounded-lg z-50 bg-neutral-100 desktop:bg-neutral-0 p-2 shadow-toast'>
      <div className='grid auto-rows-fr grid-cols-2'>
        {!cannotForwardMessageType.has(message.MsgType) && !isPending && (
          <MenuItem
            disabled={message.ReadDelete}
            className='border-b-neutral-200 odd:border-e-neutral-200'
            label={t('conversation.forward.forward')}
            onClick={handleInteraction('forward')}
          />
        )}

        {![
          ConversationMessageContentType.Notice,
          ConversationMessageContentType.AllBan,
          ConversationMessageContentType.JoinGroup,
          ConversationMessageContentType.LeaveGroup,
        ].includes(message.MsgType) &&
          !isPending && (
            <MenuItem
              className='border-b-neutral-200 odd:border-e-neutral-200'
              label={t('conversation.quote')}
              onClick={handleInteraction('quote')}
            />
          )}

        {message.MsgType === ConversationMessageContentType.Text && (
          <MenuItem
            className='border-b-neutral-200 odd:border-e-neutral-200'
            label={t('common.copy')}
            onClick={handleInteraction('copy')}
          />
        )}

        {isPending && (
          <MenuItem
            className='border-b-neutral-200 odd:border-e-neutral-200'
            label={t('conversation.retry')}
            onClick={handleInteraction('retry')}
          />
        )}

        {isMine && dayjs().diff(dayjs(message.CreateTime), 'second') <= 1800 && (
          <MenuItem
            className='border-b-neutral-200 odd:border-e-neutral-200'
            label={t('conversation.recall')}
            onClick={handleInteraction('recall')}
          />
        )}

        {![ConversationMessageContentType.Emoticon, ConversationMessageContentType.Contact].includes(
          message.MsgType,
        ) && (
          <MenuItem
            className='border-b-neutral-200 odd:border-e-neutral-200'
            label={t('conversation.save')}
            onClick={handleInteraction('save')}
          />
        )}

        {!isMine && (
          <>
            <MenuItem
              className='border-b-neutral-200 odd:border-e-neutral-200'
              label={t('conversation.report.report')}
              onClick={handleInteraction('report')}
            />

            {sessionType === 'group' && (
              <MenuItem
                className='border-b-neutral-200 odd:border-e-neutral-200'
                label={t('conversation.mention.mention')}
                onClick={handleInteraction('mention')}
              />
            )}
          </>
        )}

        {!isMine && canEditGroup && !isMemberAdmin && (
          <MenuItem
            className='border-b-neutral-200 odd:border-e-neutral-200'
            label={isMuted ? t('conversation.unmute') : t('conversation.mute')}
            onClick={handleInteraction(isMuted ? 'unmute' : 'mute')}
          />
        )}

        <MenuItem
          label={t('conversation.delete.delete')}
          className='border-b-neutral-200 odd:border-e-neutral-200'
          onClick={handleInteraction('delete')}
        />
        <MenuItem
          label={t('conversation.multi-choose')}
          className='border-b-neutral-200 odd:border-e-neutral-200'
          onClick={handleInteraction('multiselect')}
        />
      </div>

      {[ConversationMessageContentType.Image, ConversationMessageContentType.Emoticon].includes(message.MsgType) && (
        <div className='z-10 -mt-px grid auto-rows-fr grid-cols-1 border-t border-t-neutral-100'>
          <MenuItem
            className='border-t-neutral-200'
            label={t('stickers.add-to-stickers')}
            onClick={handleInteraction('addToStickers')}
          />
        </div>
      )}

      <div className={cn('z-10 -mt-px grid auto-rows-fr grid-cols-1 border-t border-t-neutral-100')}>
        {!isMine && canEditGroup && hasMemberInGroup && (
          <>
            <MenuItem
              className='border-t-neutral-200'
              label={t('conversation.kick-out')}
              onClick={handleInteraction('kickOut')}
            />

            <MenuItem
              className='border-t-neutral-200'
              label={t('conversation.kick-out-and-purge')}
              onClick={handleInteraction('kickOutAndPurge')}
            />
          </>
        )}
      </div>
    </div>
  );
}
